import { useEffect, useMemo, useState } from "react";
import { FileSpreadsheet, FileText, FolderOpen, Loader2, Pencil, Save, Search, Sheet, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel } from "@/components/KpiCard";
import { SmartTable } from "@/components/SmartTable";
import { api, errMsg, money } from "@/lib/api";

export const sansReservation = ({ reservation_id, ...e }) => e;
const exporterHistorique = (corps, format) => exporterCalcul(corps, format)
  .catch((e) => { if (corps.reservation_id && e.response?.status === 404) { return exporterCalcul(sansReservation(corps), format); } throw e; });

export const exporterCalcul = async (corps, format) => {
  const r = await api.post("/calcul-reservation/export", corps, { params: { format }, responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Calcul réservation ${corps.plateforme} ${corps.date_reservation}.${format}`;
  a.click();
  URL.revokeObjectURL(url);
};

const vide = (d) => ({ nom: "", nom_client: d?.nom_client || "", date_debut: d?.date_debut || "", logement: d?.logement || "" });

export const EnregistrerCalcul = ({ corps, onSaved, defauts }) => {
  const [v, setV] = useState(() => vide(defauts));
  const [logements, setLogements] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/logements").then((r) => setLogements(r.data)).catch(() => {}); }, []);
  const set = (k) => (e) => setV((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/calculs-reservation", { ...v, date_debut: v.date_debut || null, calcul: corps });
      toast.success("Calcul enregistré dans l'historique");
      setV(vide(defauts));
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="calcul-enregistrer-bloc">
      <Input value={v.nom} onChange={set("nom")} maxLength={120} placeholder="Nom du calcul (facultatif)" className="h-8 w-52 bg-white" data-testid="calcul-enregistrer-nom" />
      <Input value={v.nom_client} onChange={set("nom_client")} maxLength={120} placeholder="Client (facultatif)" className="h-8 w-44 bg-white" data-testid="calcul-enregistrer-client" />
      <Input type="date" value={v.date_debut} onChange={set("date_debut")} title="Date d'arrivée" className="h-8 w-40 bg-white" data-testid="calcul-enregistrer-date-sejour" />
      <Input value={v.logement} onChange={set("logement")} list="calcul-logements" maxLength={120} placeholder="Logement (facultatif)" className="h-8 w-48 bg-white" data-testid="calcul-enregistrer-logement" />
      <datalist id="calcul-logements">{logements.map((l) => <option key={l} value={l} />)}</datalist>
      <Button size="sm" className="h-8 bg-emerald-700 hover:bg-emerald-800" onClick={save} disabled={busy} data-testid="calcul-enregistrer">
        {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />}Enregistrer
      </Button>
    </div>
  );
};

const Icone = ({ title, onClick, testId, children, danger }) => (
  <button type="button" title={title} aria-label={title} onClick={onClick} data-testid={testId}
    className={`grid h-7 w-7 place-items-center rounded text-slate-500 transition-colors hover:bg-slate-100 ${danger ? "hover:text-red-600" : "hover:text-emerald-700"}`}>{children}</button>
);

const Actions = ({ h, onOuvrir, onChange }) => {
  const dl = (f) => exporterHistorique(h.entree, f).catch((e) => toast.error(errMsg(e)));
  const renommer = async () => {
    const nom = window.prompt("Nom du calcul", h.nom);
    if (nom === null) { return; }
    try { await api.patch(`/calculs-reservation/${h.id}`, { nom }); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  const suppr = async () => {
    if (!window.confirm(`Supprimer le calcul « ${h.nom || h.created_at.slice(0, 10)} » ?`)) { return; }
    try { await api.delete(`/calculs-reservation/${h.id}`); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <span className="flex justify-end gap-0.5">
      <Icone title="Rouvrir dans le calculateur" onClick={() => onOuvrir(h)} testId={`historique-ouvrir-${h.id}`}><FolderOpen className="h-3.5 w-3.5" /></Icone>
      <Icone title="PDF" onClick={() => dl("pdf")} testId={`historique-pdf-${h.id}`}><FileText className="h-3.5 w-3.5" /></Icone>
      <Icone title="CSV" onClick={() => dl("csv")} testId={`historique-csv-${h.id}`}><Sheet className="h-3.5 w-3.5" /></Icone>
      <Icone title="Excel" onClick={() => dl("xlsx")} testId={`historique-xlsx-${h.id}`}><FileSpreadsheet className="h-3.5 w-3.5" /></Icone>
      <Icone title="Renommer" onClick={renommer} testId={`historique-renommer-${h.id}`}><Pencil className="h-3.5 w-3.5" /></Icone>
      <Icone title="Supprimer" onClick={suppr} testId={`historique-supprimer-${h.id}`} danger><Trash2 className="h-3.5 w-3.5" /></Icone>
    </span>
  );
};

const Th = ({ children, left }) => <th className={`whitespace-nowrap px-3 py-2 text-[11px] font-mono uppercase tracking-wider text-slate-500 ${left ? "text-left" : "text-right"}`}>{children}</th>;

const jourLocal = (iso) => { const d = new Date(iso); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const Filtres = ({ f, set, nb, total }) => (
  <div className="mb-4 flex flex-wrap items-end gap-3" data-testid="historique-filtres">
    <label className="relative min-w-[240px] flex-1">
      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
      <Input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Rechercher par nom, client ou numéro de réservation" className="h-9 bg-white pl-8" data-testid="historique-recherche" />
    </label>
    <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1" data-testid="historique-filtre-plateforme">
      {[["", "Toutes"], ["Airbnb", "Airbnb"], ["Booking", "Booking"]].map(([v, l]) => (
        <button key={l} type="button" onClick={() => set("plateforme", v)} data-testid={`historique-filtre-plateforme-${l.toLowerCase()}`}
          className={`rounded-md px-3 py-1 text-sm transition-colors ${f.plateforme === v ? "bg-white font-semibold text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>{l}</button>
      ))}
    </div>
    <label className="text-xs text-slate-600">Enregistré du<Input type="date" value={f.du} onChange={(e) => set("du", e.target.value)} className="h-9 w-40 bg-white" data-testid="historique-filtre-du" /></label>
    <label className="text-xs text-slate-600">au<Input type="date" value={f.au} onChange={(e) => set("au", e.target.value)} className="h-9 w-40 bg-white" data-testid="historique-filtre-au" /></label>
    <span className="pb-2 text-xs text-slate-500" data-testid="historique-compte">{nb} / {total} calcul(s)</span>
  </div>
);

const filtrer = (liste, f) => {
  const q = norm(f.q.trim());
  return liste.filter((h) => {
    const jour = jourLocal(h.created_at);
    if (f.plateforme && h.resume.plateforme !== f.plateforme) { return false; }
    if ((f.du && jour < f.du) || (f.au && jour > f.au)) { return false; }
    return !q || [h.nom, h.resume.nom_client, h.resume.code].some((s) => norm(s).includes(q));
  });
};

const Sejour = ({ r }) => (r.date_debut ? <>{r.date_debut}{r.date_fin ? <span className="block text-xs text-slate-500">→ {r.date_fin}</span> : null}</> : "—");

export const HistoriqueCalculs = ({ liste, onOuvrir, onChange }) => {
  const [f, setF] = useState({ q: "", plateforme: "", du: "", au: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const lignes = useMemo(() => (liste ? filtrer(liste, f) : []), [liste, f]);
  return (
  <Panel title="Historique des calculs" testId="historique-calculs">
    {!liste ? <Loader2 className="h-5 w-5 animate-spin text-emerald-700" /> : !liste.length ? (
      <p className="text-sm text-slate-500" data-testid="historique-vide">Aucun calcul enregistré. Cliquez sur « Enregistrer » sous le résultat pour garder un calcul.</p>
    ) : (
      <>
      <Filtres f={f} set={set} nb={lignes.length} total={liste.length} />
      {!lignes.length ? <p className="text-sm text-slate-500" data-testid="historique-aucun-resultat">Aucun calcul ne correspond à la recherche.</p> : (
      <div className="-mx-5 overflow-x-auto">
        <SmartTable id="historique-calculs-table" data-export-titre="Historique des calculs" className="w-full min-w-[1200px] text-sm" data-testid="historique-table">
          <thead><tr><Th left>Date</Th><Th left>Nom</Th><Th left>Séjour</Th><Th left>Logement</Th><Th left>Plateforme</Th><Th>Nuits</Th><Th>Loyer</Th><Th>Total exact</Th><Th>Total plateforme</Th><Th>Écart</Th><Th>Net exact</Th><th data-st-export="non" /></tr></thead>
          <tbody>
            {lignes.map((h) => (
              <tr key={h.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`historique-ligne-${h.id}`}>
                <td className="px-3 py-2.5 tabular text-slate-600">{jourLocal(h.created_at)}</td>
                <td className="px-3 py-2.5 font-medium">{h.nom || "—"}{(h.resume.code || h.resume.nom_client) && <span className="block text-xs font-normal text-slate-500">{[h.resume.code && `Réservation ${h.resume.code}`, h.resume.nom_client].filter(Boolean).join(" · ")}</span>}</td>
                <td className="whitespace-nowrap px-3 py-2.5 tabular text-slate-600" data-testid={`historique-sejour-${h.id}`}><Sejour r={h.resume} /></td>
                <td className="px-3 py-2.5 text-slate-600" data-testid={`historique-logement-${h.id}`}>{h.resume.logement || "—"}</td>
                <td className="px-3 py-2.5">{h.resume.plateforme}{h.resume.sens === "inverse" ? " · inversé" : ""}</td>
                <td className="px-3 py-2.5 text-right tabular">{`${h.resume.nuits} n.`}</td>
                <td className="px-3 py-2.5 text-right tabular">{money(h.resume.loyer)}</td>
                <td className="px-3 py-2.5 text-right tabular">{money(h.resume.total_exact)}</td>
                <td className="px-3 py-2.5 text-right tabular">{money(h.resume.total_erreur)}</td>
                <td className="px-3 py-2.5 text-right tabular text-amber-700">{money(h.resume.ecart_taxes)}</td>
                <td className="px-3 py-2.5 text-right tabular font-medium">{money(h.resume.net_exact)}</td>
                <td className="px-2 py-1.5"><Actions h={h} onOuvrir={onOuvrir} onChange={onChange} /></td>
              </tr>
            ))}
          </tbody>
        </SmartTable>
      </div>
      )}
      </>
    )}
  </Panel>
  );
};
